/**
 * Header Component
 * Feature: 013-shell-navigation Phase 9
 *
 * Simplified header with logo only.
 * Profile and Logout are now accessed via /profile route (sidebar/bottom-nav).
 *
 * The multi-patient switcher pill is rendered only when the currently active
 * route (or any ancestor) declares `data: { showPatientPill: true }`. This
 * scopes the pill to Pulse routes today; other features can opt-in later by
 * setting the same flag in their route config. The pill itself still applies
 * its own 2+ patients visibility rule.
 */

import { Component, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterModule
} from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { HeaderPatientPillComponent } from '../header-patient-pill/header-patient-pill.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    RouterModule,
    HeaderPatientPillComponent
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);

  /**
   * True when the active route (or any ancestor) sets
   * `data: { showPatientPill: true }`. Recomputed on every NavigationEnd so
   * the pill appears/disappears as the user navigates.
   */
  readonly showPatientPill = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      startWith(null),
      map(() => this.routeDeclaresPatientPill(this.activatedRoute))
    ),
    { initialValue: this.routeDeclaresPatientPill(this.activatedRoute) }
  );

  private routeDeclaresPatientPill(root: ActivatedRoute): boolean {
    let current: ActivatedRoute | null = root;
    while (current) {
      if (current.snapshot?.data?.['showPatientPill'] === true) {
        return true;
      }
      current = current.firstChild;
    }
    return false;
  }
}
